/**
 * Task State Machine — Execution Engine
 *
 * Defines states, transitions, events, and available actions for workflow tasks.
 * Maps to DB statuses: waiting, pending, in_progress, completed, cancelled, failed, skipped
 * Frontend extends with 'paused' (stored via metadata/events, mapped in queries).
 */

// ─── States ───────────────────────────────────────────────────────────────────

export type TaskState =
  | 'waiting'      // Aguardando predecessor
  | 'pending'      // Pronta para início (atribuída ou na fila)
  | 'unassigned'   // Pendente sem executor
  | 'in_progress'  // Em execução ativa
  | 'paused'       // Pausada pelo operador
  | 'completed'    // Concluída
  | 'cancelled'    // Cancelada
  | 'failed'       // Falhou
  | 'skipped';     // Pulada

// ─── Events ───────────────────────────────────────────────────────────────────

export type TaskEvent =
  | 'TASK_UNLOCKED'      // Predecessor concluiu → waiting → pending
  | 'TASK_ASSIGNED'      // Responsável atribuído
  | 'TASK_CLAIMED'       // Operador puxou da fila
  | 'TASK_STARTED'       // Início da execução
  | 'TASK_PAUSED'        // Pausa manual
  | 'TASK_RESUMED'       // Retomada após pausa
  | 'TASK_COMPLETED'     // Conclusão com sucesso
  | 'TASK_REJECTED'      // Devolvida (approve/reject flow)
  | 'TASK_TRANSFERRED'   // Transferida para outro operador
  | 'TASK_ESCALATED'     // Escalonada para nível superior
  | 'TASK_CANCELLED'     // Cancelada administrativamente
  | 'TASK_FAILED'        // Falha na execução
  | 'TASK_SKIPPED';      // Etapa ignorada

// ─── Transition Rules ─────────────────────────────────────────────────────────

type TransitionMap = Partial<Record<TaskEvent, TaskState>>;

const TRANSITIONS: Record<TaskState, TransitionMap> = {
  waiting: {
    TASK_UNLOCKED: 'pending',
    TASK_CANCELLED: 'cancelled',
    TASK_SKIPPED: 'skipped',
  },
  unassigned: {
    TASK_CLAIMED: 'pending',
    TASK_ASSIGNED: 'pending',
    TASK_CANCELLED: 'cancelled',
    TASK_SKIPPED: 'skipped',
  },
  pending: {
    TASK_STARTED: 'in_progress',
    TASK_ASSIGNED: 'pending',
    TASK_CLAIMED: 'pending',
    TASK_CANCELLED: 'cancelled',
    TASK_SKIPPED: 'skipped',
  },
  in_progress: {
    TASK_PAUSED: 'paused',
    TASK_COMPLETED: 'completed',
    TASK_REJECTED: 'pending',
    TASK_TRANSFERRED: 'pending',
    TASK_ESCALATED: 'in_progress', // escalation doesn't change state
    TASK_FAILED: 'failed',
    TASK_CANCELLED: 'cancelled',
  },
  paused: {
    TASK_RESUMED: 'in_progress',
    TASK_TRANSFERRED: 'pending',
    TASK_ESCALATED: 'paused',
    TASK_CANCELLED: 'cancelled',
  },
  completed: {},
  cancelled: {},
  failed: {
    TASK_STARTED: 'in_progress', // retry
    TASK_CANCELLED: 'cancelled',
  },
  skipped: {},
};

export function canTransition(current: TaskState, event: TaskEvent): boolean {
  return TRANSITIONS[current]?.[event] !== undefined;
}

export function getNextState(current: TaskState, event: TaskEvent): TaskState | null {
  return TRANSITIONS[current]?.[event] ?? null;
}

// ─── UI Action Definitions ────────────────────────────────────────────────────

export type TaskAction =
  | 'claim'
  | 'start'
  | 'pause'
  | 'resume'
  | 'complete'
  | 'reject'
  | 'transfer'
  | 'escalate'
  | 'register_problem'
  | 'cancel';

export interface ActionSpec {
  action: TaskAction;
  label: string;
  event: TaskEvent;
  /** Whether this action requires an executor to be assigned */
  requiresExecutor: boolean;
  /** Whether action is primary (highlighted) vs secondary */
  primary: boolean;
  /** Group for UI hierarchy */
  group: 'primary' | 'secondary' | 'operational' | 'support';
}

// ─── Action Matrix per State ──────────────────────────────────────────────────

export interface TaskContext {
  hasExecutor: boolean;
  isCurrentUser: boolean;
  completionType: 'simple_complete' | 'approve_reject';
}

/**
 * Resolves the effective UI state from raw DB status + context.
 * Enforces invariant: IN_PROGRESS without executor → UNASSIGNED.
 */
export function resolveEffectiveState(
  rawStatus: string,
  hasExecutor: boolean,
): TaskState {
  // pending without executor = unassigned
  if (rawStatus === 'pending' && !hasExecutor) return 'unassigned';
  // in_progress without executor = invalid → treat as unassigned
  if (rawStatus === 'in_progress' && !hasExecutor) return 'unassigned';
  return rawStatus as TaskState;
}

const ALL_ACTIONS: ActionSpec[] = [
  { action: 'claim',            label: 'Assumir tarefa',       event: 'TASK_CLAIMED',     requiresExecutor: false, primary: true,  group: 'primary' },
  { action: 'start',            label: 'Iniciar execução',     event: 'TASK_STARTED',     requiresExecutor: true,  primary: true,  group: 'primary' },
  { action: 'complete',         label: 'Concluir etapa',       event: 'TASK_COMPLETED',   requiresExecutor: true,  primary: true,  group: 'primary' },
  { action: 'resume',           label: 'Retomar execução',     event: 'TASK_RESUMED',     requiresExecutor: true,  primary: true,  group: 'primary' },
  { action: 'pause',            label: 'Pausar',               event: 'TASK_PAUSED',      requiresExecutor: true,  primary: false, group: 'secondary' },
  { action: 'reject',           label: 'Devolver',             event: 'TASK_REJECTED',    requiresExecutor: true,  primary: false, group: 'secondary' },
  { action: 'transfer',         label: 'Transferir',           event: 'TASK_TRANSFERRED', requiresExecutor: true,  primary: false, group: 'operational' },
  { action: 'escalate',         label: 'Escalar',              event: 'TASK_ESCALATED',   requiresExecutor: false, primary: false, group: 'operational' },
  { action: 'register_problem', label: 'Registrar problema',   event: 'TASK_FAILED',      requiresExecutor: true,  primary: false, group: 'support' },
  { action: 'cancel',           label: 'Cancelar',             event: 'TASK_CANCELLED',   requiresExecutor: false, primary: false, group: 'support' },
];

/** Actions available in each state (action keys) */
const STATE_ACTIONS: Record<TaskState, TaskAction[]> = {
  waiting:     [],
  unassigned:  ['claim'],
  pending:     ['start', 'escalate', 'cancel'],
  in_progress: ['complete', 'reject', 'pause', 'transfer', 'escalate', 'register_problem'],
  paused:      ['resume', 'transfer', 'escalate', 'cancel'],
  completed:   [],
  cancelled:   [],
  failed:      ['start', 'escalate', 'cancel'],
  skipped:     [],
};

/** Tooltip explanations for disabled actions */
export const ACTION_DISABLED_TOOLTIPS: Record<TaskAction, string> = {
  claim:            'Tarefa já possui responsável',
  start:            'Disponível após assumir a tarefa',
  pause:            'Apenas o executor pode pausar',
  resume:           'Apenas o executor pode retomar',
  complete:         'Inicie a tarefa antes de concluir',
  reject:           'Inicie a tarefa antes de devolver',
  transfer:         'Em desenvolvimento',
  escalate:         'Disponível apenas durante execução',
  register_problem: 'Apenas o executor pode registrar problemas',
  cancel:           'Em desenvolvimento',
};

/**
 * Returns ordered list of available actions for a task given its state and context.
 * Filters by executor requirements and state validity.
 */
export function getAvailableActions(state: TaskState, ctx: TaskContext): ActionSpec[] {
  const allowedKeys = STATE_ACTIONS[state] ?? [];

  return ALL_ACTIONS
    .filter((spec) => allowedKeys.includes(spec.action))
    .filter((spec) => {
      // If action requires executor and there's none, hide it
      if (spec.requiresExecutor && !ctx.hasExecutor) return false;

      // Permission: only executor (current user) can pause/complete/register_problem
      if (['pause', 'complete', 'reject', 'register_problem'].includes(spec.action) && !ctx.isCurrentUser) return false;

      return true;
    });
}

/**
 * Returns the single primary action for the current state (the big button).
 */
export function getPrimaryAction(state: TaskState, ctx: TaskContext): ActionSpec | null {
  const actions = getAvailableActions(state, ctx);
  return actions.find((a) => a.primary) ?? null;
}

/**
 * Returns secondary actions grouped by category.
 */
export function getGroupedActions(state: TaskState, ctx: TaskContext): {
  primary: ActionSpec | null;
  secondary: ActionSpec[];
  operational: ActionSpec[];
  support: ActionSpec[];
} {
  const actions = getAvailableActions(state, ctx);
  return {
    primary: actions.find((a) => a.primary) ?? null,
    secondary: actions.filter((a) => a.group === 'secondary'),
    operational: actions.filter((a) => a.group === 'operational'),
    support: actions.filter((a) => a.group === 'support'),
  };
}

// ─── State Display ────────────────────────────────────────────────────────────

export interface StateDisplay {
  label: string;
  color: 'slate' | 'blue' | 'amber' | 'emerald' | 'red' | 'purple';
  dotAnimated: boolean;
}

export function getStateDisplay(state: TaskState): StateDisplay {
  switch (state) {
    case 'waiting':     return { label: 'Aguardando',  color: 'slate',   dotAnimated: false };
    case 'unassigned':  return { label: 'Sem responsável', color: 'amber', dotAnimated: false };
    case 'pending':     return { label: 'Pendente',    color: 'slate',   dotAnimated: false };
    case 'in_progress': return { label: 'Executando',  color: 'blue',    dotAnimated: true };
    case 'paused':      return { label: 'Pausada',     color: 'amber',   dotAnimated: false };
    case 'completed':   return { label: 'Concluída',   color: 'emerald', dotAnimated: false };
    case 'cancelled':   return { label: 'Cancelada',   color: 'red',     dotAnimated: false };
    case 'failed':      return { label: 'Falha',       color: 'red',     dotAnimated: false };
    case 'skipped':     return { label: 'Ignorada',    color: 'purple',  dotAnimated: false };
  }
}

// ─── Invariant Validation ─────────────────────────────────────────────────────

export interface TaskInvariantResult {
  valid: boolean;
  violations: string[];
}

export function validateTaskInvariants(state: TaskState, hasExecutor: boolean): TaskInvariantResult {
  const violations: string[] = [];

  if (state === 'in_progress' && !hasExecutor) {
    violations.push('IN_PROGRESS requires an assigned executor');
  }

  if (state === 'paused' && !hasExecutor) {
    violations.push('PAUSED requires an assigned executor');
  }

  return { valid: violations.length === 0, violations };
}
